import React, { useState, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { LoaderCircle } from 'lucide-react';
import _ from 'lodash';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Label,
} from '@cargomatic/ui';
import { showToast } from 'stores/ToastStore';
import AddButton from 'components/v2/buttons/AddButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import CargomaticSelect from 'components/v2/select';
import CargomaticInput from 'components/v2/input';
import {
  createException,
  useGetBridgeReasonCodes,
} from 'utils/BridgeCodeAPIUtils';
import { sluggify } from 'utils/localization/utils';

/**
 * Initial default state for bridge code row
 */
const INIT_BRIDGE_CODE = {
  shipment_leg: '',
  bridge_code: '',
  reason_code: '',
  reason_description: '',
};

const DUPLICATE_COMBINATIONS_KEYS = [
  'shipment_leg',
  'bridge_code',
  'reason_code',
];

/**
 * COLUMN constants
 */
const CODES_COLUMN = {
  REASON_CODES: 'reasonCodes',
  BRIDGE_CODES: 'bridgeCodes',
  SHIPMENT_LEGS: 'shipmentLegs',
};

/**
 * Reset field mapping
 */
const FIELD_RESET_MAP = {
  shipment_leg: ['bridge_code', 'reason_code'],
  bridge_code: ['reason_code'],
  reason_code: [],
  reason_description: [],
};

function BulkShipmentBridges({ t, isOpen, onClose, shipments = [] }) {
  /**
   * State definitions
   */
  const [exceptionList, setExceptionList] = useState([]);
  const [exception, setException] = useState(INIT_BRIDGE_CODE);
  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);
  const [defaultDataLoadingFailed, setDefaultDataLoadingFailed] =
    useState(false);

  /**
   * Get the default bridge codes for dropdowns
   */
  const { data: defaultBridgeCodes, isLoading: defaultBridgeCodesLoading } =
    useGetBridgeReasonCodes({
      onError: () => {
        setErrorText('FAILED_TO_LOAD_DEFAULT_BRIDGE_CODES');
        setDefaultDataLoadingFailed(true);
        showToast(t('FAILED_TO_LOAD_DEFAULT_BRIDGE_CODES'), '', 'error');
      },
    });

  /**
   * Get the shipment legs for drayage shipment from default bridge codes
   */
  const shipmentLegs = useMemo(() => {
    const drayageCategory =
      defaultBridgeCodes?.find((item) => item.lineOfBusiness === 'drayage') ||
      [];
    const legs = drayageCategory?.categories
      ?.filter((item) => item.categoryType === 'market_legs')
      ?.map((leg) => {
        return {
          ...leg,
          value: leg?.name,
          localizationSlug: sluggify(leg?.name),
        };
      });
    return legs;
  }, [defaultBridgeCodes]);

  /**
   * Do clean up after modal closed
   */
  const cleanUpStates = () => {
    setExceptionList([]);
    setException(INIT_BRIDGE_CODE);
    setErrorText('');
    onClose();
  };

  /**
   * Get dropdown menu items
   */
  const getDropDownItems = (
    itemName = '',
    shipment_leg = '',
    bridge_code = '',
  ) => {
    if (!itemName) return [];

    // only shipment legs required
    if (itemName === CODES_COLUMN.SHIPMENT_LEGS) {
      return shipmentLegs || [];
    }

    // Bridge code dropdown
    if (itemName === CODES_COLUMN.BRIDGE_CODES && shipment_leg) {
      const foundLeg = shipmentLegs?.find((leg) => leg?.value === shipment_leg);
      return (
        foundLeg?.subCategories?.map(({ reasonType }) => ({
          value: reasonType,
        })) || []
      );
    }

    // Reason code dropdown
    if (itemName === CODES_COLUMN.REASON_CODES && shipment_leg && bridge_code) {
      const foundLeg = shipmentLegs?.find((leg) => leg?.value === shipment_leg);
      const foundBridge = foundLeg?.subCategories?.find(
        (bridge) => bridge?.reasonType === bridge_code,
      );
      return (
        foundBridge?.reasonCodes?.map(({ description }) => ({
          value: description,
        })) || []
      );
    }

    // default;
    return [];
  };

  /**
   * Function to check if a row is complete (has all required fields)
   */
  const isRowComplete = (exceptionItem) => {
    const { shipment_leg, bridge_code, reason_code } = exceptionItem;

    // Check if basic required fields are filled
    if (!shipment_leg || !bridge_code) {
      return false;
    }

    // Check if reason code is required and provided
    const reasonCodes = getDropDownItems(
      CODES_COLUMN.REASON_CODES,
      shipment_leg,
      bridge_code,
    );
    if (reasonCodes.length > 0 && !reason_code) {
      return false;
    }

    return true;
  };

  /**
   * Get incomplete rows for highlighting
   */
  const getIncompleteRows = () => {
    const incompleteIndices = [];

    exceptionList.forEach((exceptionItem, index) => {
      if (!isRowComplete(exceptionItem)) {
        incompleteIndices.push(index);
      }
    });

    return incompleteIndices;
  };

  /**
   * Function to check if a combination is duplicate
   */
  const isDuplicateCombination = (currentException, currentIndex = -1) => {
    const match = _.pick(currentException, DUPLICATE_COMBINATIONS_KEYS);

    return exceptionList.some((obj, index) => {
      // Skip checking against itself when editing
      if (index === currentIndex) return false;
      return _.isMatch(obj, match);
    });
  };

  /**
   * Get duplicate rows for highlighting
   */
  const getDuplicateRows = () => {
    const duplicateIndices = [];

    exceptionList.forEach((exception, index) => {
      if (isDuplicateCombination(exception, index)) {
        duplicateIndices.push(index);
      }
    });

    return duplicateIndices;
  };

  /**
   * Check if any duplicate row created or incompleted row exists
   */
  const duplicateRows = getDuplicateRows();
  const incompleteRows = getIncompleteRows();

  /**
   * Check if save button should be disabled
   */
  const isSaveDisabled = useMemo(() => {
    return (
      !exceptionList?.length ||
      duplicateRows?.length > 0 ||
      incompleteRows?.length > 0 ||
      defaultDataLoadingFailed
    );
  }, [exceptionList, duplicateRows, incompleteRows, defaultDataLoadingFailed]);

  /**
   * Add new exception into list
   */
  const handleAddBridgeCode = () => {
    const match = _.pick(exception, DUPLICATE_COMBINATIONS_KEYS);
    // Check if any object matches
    const exists = _.some(exceptionList, (obj) => _.isMatch(obj, match));
    if (exists) {
      setErrorText('DUPLICATE_COMBINATIONS_FOUND');
      return;
    }
    setExceptionList((prev) => [exception, ...prev]);
    setErrorText('');
    setException(INIT_BRIDGE_CODE);
  };

  /**
   * Handle row change
   */
  const handleExceptionUpdate = (field, value, index) => {
    setExceptionList((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        const updates = { [field]: value };

        // Reset dependent fields
        FIELD_RESET_MAP[field]?.forEach((resetField) => {
          updates[resetField] = '';
        });

        return { ...item, ...updates };
      }),
    );
  };

  /**
   * Remove row from the list
   */
  const handleDeleteException = (index) => {
    setExceptionList((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Check if reason code present for given brigde code
   * then only make reason code mandtaory
   */
  const getReasonCodesForCurrentSelection = () => {
    const reasonCodes = getDropDownItems(
      CODES_COLUMN.REASON_CODES,
      exception?.shipment_leg,
      exception?.bridge_code,
    );
    return !!reasonCodes?.length;
  };

  /**
   * Create Payload for backend request &
   *  once all validation passed
   */
  const handleSave = async () => {
    setLoading(true);
    try {
      const shipmentIds = shipments?.map((shipment) => shipment.id);
      const payload = { create: [] };
      shipmentIds.forEach((shipment_id) => {
        exceptionList.forEach((exception) => {
          payload.create.push({ ...exception, shipment_id });
        });
      });

      const response = await createException(payload);
      if (response.success) {
        showToast(t('MARKET_EXCEPTION_CREATION_SUCCESS'));
      } else {
        showToast(t('MARKET_EXCEPTION_CREATION_FAIL'), '', 'error');
      }
      cleanUpStates();
    } catch (_error) {
      showToast(t('MARKET_EXCEPTION_CREATION_FAIL'), '', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl">
        <DialogHeader>
          <DialogTitle>{t('BULK_ADD_BRIDGE_CODES')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('BULK_ADD_BRIDGE_CODES')}{' '}
            {t('BULK_SHIPMENT_BRIDGES_NOTE_WITH_COUNT', {
              count: shipments?.length || 0,
            })}
          </DialogDescription>
          <Label className="block text-sm font-medium text-gray-700 pt-2">
            {t('BULK_SHIPMENT_BRIDGES_NOTE_WITH_COUNT', {
              count: shipments?.length || 0,
            })}
          </Label>
        </DialogHeader>
        {!!(
          errorText?.length ||
          duplicateRows?.length ||
          incompleteRows?.length ||
          defaultDataLoadingFailed
        ) && (
          <Alert
            type="error"
            className="alert-error bg-red-100 text-red-900 font-medium"
            data-testid="alert"
          >
            {t(errorText) ||
              (duplicateRows.length > 0 && t('DUPLICATE_COMBINATIONS_FOUND')) ||
              (incompleteRows.length > 0 &&
                t('INCOMPLETE_ROWS_FOUND', { count: incompleteRows.length })) ||
              t('VALIDATION_ERRORS_FOUND')}
          </Alert>
        )}
        {defaultBridgeCodesLoading && (
          <LoaderCircle
            className="w-4 h-4 animate-spin ml-2"
            data-testid="LoaderCircle-x-f4ff494b"
          />
        )}
        {!defaultBridgeCodesLoading && (
          <>
            <Table className="table-fixed w-full">
              <TableHeader className="sticky top-0">
                <TableRow>
                  <TableHead
                    className="w-[22.5%]"
                    data-testid="TableHead-x-f4ff494b"
                  >
                    {t('SHIPMENT_LEG')}
                  </TableHead>
                  <TableHead
                    className="w-[22.5%]"
                    data-testid="TableHead-x-f4ff494t"
                  >
                    {t('BRIDGE')}
                  </TableHead>
                  <TableHead
                    className="w-[22.5%]"
                    data-testid="TableHead-x-f4ff494u"
                  >
                    {t('BRIDGE_REASON_CODE')}
                  </TableHead>
                  <TableHead
                    className="min-w-[22.5%]"
                    data-testid="TableHead-x-f4ff494r"
                  >
                    {t('NOTES')}
                  </TableHead>
                  <TableHead
                    className="w-[10%]"
                    data-testid="TableHead-x-f4ff494i"
                  >
                    {t('ACTION')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="sticky top-0">
                  <TableCell>
                    <CargomaticSelect
                      dataTestId="CargomaticSelect-x-92dc5d28"
                      showCaption
                      hideLabel
                      items={getDropDownItems(CODES_COLUMN.SHIPMENT_LEGS)}
                      selected={exception?.shipment_leg || ''}
                      className="w-full"
                      inputClassNames="max-w-full min-w-full"
                      onSelect={({ value }) => {
                        setException({
                          ...exception,
                          shipment_leg: value,
                          bridge_code: '',
                          reason_code: '',
                        });
                      }}
                      applyDefaultMargins={false}
                      valueKey="value"
                      textKey="localizationSlug"
                      caption={t('SELECT_LEG')}
                      disabled={defaultDataLoadingFailed}
                    />
                  </TableCell>
                  <TableCell>
                    <CargomaticSelect
                      dataTestId="CargomaticSelect-x-6c1cb9a8"
                      showCaption
                      hideLabel
                      items={getDropDownItems(
                        CODES_COLUMN.BRIDGE_CODES,
                        exception?.shipment_leg,
                      )}
                      selected={exception?.bridge_code || ''}
                      className="w-full"
                      inputClassNames="max-w-full min-w-full"
                      onSelect={({ value }) => {
                        setException({
                          ...exception,
                          bridge_code: value,
                          reason_code: '',
                        });
                      }}
                      applyDefaultMargins={false}
                      valueKey="value"
                      textKey="value"
                      caption={t('SELECT_BRIDGE')}
                      disabled={!exception?.shipment_leg}
                    />
                  </TableCell>
                  <TableCell>
                    <CargomaticSelect
                      dataTestId="CargomaticSelect-x-1d50d4d0"
                      showCaption
                      hideLabel
                      items={getDropDownItems(
                        CODES_COLUMN.REASON_CODES,
                        exception?.shipment_leg,
                        exception?.bridge_code,
                      )}
                      selected={exception?.reason_code || ''}
                      className="w-full"
                      inputClassNames="max-w-full min-w-full"
                      onSelect={({ value }) => {
                        setException({ ...exception, reason_code: value });
                      }}
                      applyDefaultMargins={false}
                      valueKey="value"
                      textKey="value"
                      caption={t('SELECT_REASON_CODE')}
                      disabled={!exception?.bridge_code}
                    />
                  </TableCell>
                  <TableCell>
                    <CargomaticInput
                      dataTestId="CargomaticInput-x-62f5540f"
                      hideLabel
                      type="text"
                      name="template-name"
                      className="w-full"
                      placeholder={t('NOTES')}
                      value={exception?.reason_description}
                      onChange={(value) => {
                        setException({
                          ...exception,
                          reason_description: value,
                        });
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <AddButton
                      dataTestId="AddButton-x-0ed24a4d"
                      onClick={handleAddBridgeCode}
                      disabled={
                        !exception?.bridge_code ||
                        !exception?.shipment_leg ||
                        // Only require reason code if there are reason codes available
                        (getReasonCodesForCurrentSelection() &&
                          !exception?.reason_code)
                      }
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <div className="max-h-[40vh] overflow-y-auto">
              <Table className="table-fixed ">
                <TableHeader className="sticky top-0">
                  <TableRow>
                    <TableHead
                      className="w-[22.5%] h-0"
                      data-testid="TableHead-x-f4ff494b"
                    ></TableHead>
                    <TableHead
                      className="w-[22.5%] h-0"
                      data-testid="TableHead-x-f4ff494t"
                    ></TableHead>
                    <TableHead
                      className="w-[22.5%] h-0"
                      data-testid="TableHead-x-f4ff494u"
                    ></TableHead>
                    <TableHead
                      className="min-w-[22.5%] h-0"
                      data-testid="TableHead-x-f4ff494r"
                    ></TableHead>
                    <TableHead
                      className="w-[10%] h-0"
                      data-testid="TableHead-x-f4ff494i"
                    ></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(exceptionList || [])?.map(
                    (
                      {
                        shipment_leg,
                        bridge_code,
                        reason_code,
                        reason_description,
                      },
                      index,
                    ) => {
                      const isDuplicate = duplicateRows?.includes(index);
                      const isIncomplete = incompleteRows?.includes(index);
                      const hasError = isDuplicate || isIncomplete;
                      const hasErrorClassNames = hasError
                        ? 'border border-red-300 rounded-md'
                        : '';
                      return (
                        <TableRow
                          key={index}
                          className={hasError ? 'bg-red-50 border-red-200' : ''}
                        >
                          <TableCell>
                            <CargomaticSelect
                              dataTestId={`CargomaticSelect-92dc5d28-${index}`}
                              showCaption
                              hideLabel
                              items={getDropDownItems(
                                CODES_COLUMN.SHIPMENT_LEGS,
                              )}
                              selected={shipment_leg || ''}
                              className="w-full"
                              inputClassNames={`max-w-full min-w-full ${hasErrorClassNames}`}
                              onSelect={({ value }) => {
                                handleExceptionUpdate(
                                  'shipment_leg',
                                  value,
                                  index,
                                );
                              }}
                              applyDefaultMargins={false}
                              valueKey="value"
                              textKey="localizationSlug"
                              caption={t('SELECT_LEG')}
                            />
                          </TableCell>
                          <TableCell>
                            <CargomaticSelect
                              dataTestId={`CargomaticSelect-6c1cb9a8-${index}`}
                              showCaption
                              hideLabel
                              items={getDropDownItems(
                                CODES_COLUMN.BRIDGE_CODES,
                                shipment_leg,
                              )}
                              selected={bridge_code || ''}
                              className="w-full"
                              inputClassNames={`max-w-full min-w-full ${hasErrorClassNames}`}
                              onSelect={({ value }) => {
                                handleExceptionUpdate(
                                  'bridge_code',
                                  value,
                                  index,
                                );
                              }}
                              applyDefaultMargins={false}
                              valueKey="value"
                              textKey="value"
                              caption={t('SELECT_BRIDGE')}
                              disabled={!shipment_leg}
                            />
                          </TableCell>
                          <TableCell>
                            <CargomaticSelect
                              dataTestId={`CargomaticSelect-1d50d4d0-${index}`}
                              showCaption
                              hideLabel
                              items={getDropDownItems(
                                CODES_COLUMN.REASON_CODES,
                                shipment_leg,
                                bridge_code,
                              )}
                              selected={reason_code || ''}
                              className="w-full"
                              inputClassNames={`max-w-full min-w-full ${hasErrorClassNames}`}
                              onSelect={({ value }) => {
                                handleExceptionUpdate(
                                  'reason_code',
                                  value,
                                  index,
                                );
                              }}
                              applyDefaultMargins={false}
                              valueKey="value"
                              textKey="value"
                              caption={t('SELECT_REASON_CODE')}
                              disabled={!bridge_code}
                            />
                          </TableCell>
                          <TableCell>
                            <CargomaticInput
                              dataTestId={`CargomaticInput-62f5540f-${index}`}
                              hideLabel
                              type="text"
                              name="template-name"
                              className="w-full"
                              inputClassNames={`max-w-full min-w-full ${hasErrorClassNames}`}
                              placeholder={t('NOTES')}
                              value={reason_description}
                              onChange={(value) => {
                                handleExceptionUpdate(
                                  'reason_description',
                                  value,
                                  index,
                                );
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <DeleteButton
                              dataTestId={`delete-btn-x2f5540f-${index}`}
                              onClick={() => handleDeleteException(index)}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    },
                  )}
                </TableBody>
              </Table>
            </div>
            <DialogFooter>
              <div className="flex justify-end items-center gap-4">
                <Button
                  variant="secondary"
                  type="submit"
                  onClick={onClose}
                  className="flex items-center justify-center gap-2 w-20"
                  data-testid="buk-bridge-code-cancel-x2f5540f"
                >
                  {t('CANCEL')}
                </Button>
                <Button
                  type="submit"
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                  className="flex items-center justify-center gap-2 w-20"
                  data-testid="buk-bridge-code-save-x2f5540f"
                >
                  {loading && <LoaderCircle className="w-4 h-4 animate-spin" />}
                  {t('SAVE')}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default withTranslation()(BulkShipmentBridges);
